import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { anlagenAPI, wartungenAPI, kundenAPI } from '../services/api';
import { ArrowLeft, Save, AlertCircle, Info, User, Building2 } from 'lucide-react';
import Navbar from '../components/Navbar';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// GWP-Werte für gängige Kältemittel (aktualisiert 2024)
const GWP_WERTE = {
  'R 410A': 2088,
  'R 407C': 1774,
  'R 134a': 1430,
  'R 404A': 3922,
  'R 507A': 3985,
  'R 32': 675,
  'R 454B': 466,
  'R 452B': 698,
  'R 454A': 238,
  'R 1234yf': 4,
  'R 1234ze': 6,
  'R 290': 3,  // Propan
  'R 600a': 3,  // Isobutan
  'R 717': 0,  // Ammoniak
  'R 744': 1,  // CO2
};

const WartungFormNeu = () => {
  const { anlageId } = useParams();
  const navigate = useNavigate();

  const [anlage, setAnlage] = useState(null);
  const [kunde, setKunde] = useState(null);
  const [vorlage, setVorlage] = useState(null);
  const [formData, setFormData] = useState({
    wartungsdatum: new Date().toISOString().slice(0, 16)
  });
  const [vorlageDaten, setVorlageDaten] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Anlagen-spezifische Daten
  const [kaeltemittel, setKaeltemittel] = useState('');
  const [kaeltemittelmenge, setKaeltemittelmenge] = useState('');
  const [gwpWert, setGwpWert] = useState('');
  const [co2Aequivalent, setCo2Aequivalent] = useState('');

  useEffect(() => {
    loadData();
  }, [anlageId]);

  const loadData = async () => {
    try {
      const anlageData = await anlagenAPI.getById(anlageId);
      setAnlage(anlageData);

      // Lade Kundendaten wenn vorhanden
      if (anlageData.customer_id) {
        const kundeData = await kundenAPI.getById(anlageData.customer_id);
        setKunde(kundeData);
      }

      // Setze Kältemittel-Daten aus Anlage
      const km = anlageData.kaeltemittel || '';
      const kmMenge = anlageData.kaeltemittelmenge || '';
      
      setKaeltemittel(km);
      setKaeltemittelmenge(kmMenge);
      
      // Automatisch GWP setzen wenn Kältemittel bekannt
      if (km && GWP_WERTE[km]) {
        setGwpWert(GWP_WERTE[km].toString());
        // Berechne CO2-Äquivalent wenn auch Menge vorhanden
        if (kmMenge) {
          const co2 = (parseFloat(kmMenge) * GWP_WERTE[km] / 1000).toFixed(3);
          setCo2Aequivalent(co2);
        }
      }

      // Lade die passende Vorlage
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/vorlagen/${anlageData.anlagentyp}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVorlage(response.data);
    } catch (error) {
      console.error('Fehler beim Laden:', error);
      setError('Daten konnten nicht geladen werden');
    }
  };

  // Automatische Berechnung bei Änderung von Kältemittel oder Menge
  useEffect(() => {
    if (kaeltemittel && kaeltemittelmenge && gwpWert) {
      const menge = parseFloat(kaeltemittelmenge);
      const gwp = parseFloat(gwpWert);
      if (!isNaN(menge) && !isNaN(gwp) && menge > 0 && gwp > 0) {
        const co2 = (menge * gwp / 1000).toFixed(3);
        setCo2Aequivalent(co2);
      }
    }
  }, [kaeltemittel, kaeltemittelmenge, gwpWert]);

  // Setze GWP automatisch wenn Kältemittel gewählt wird
  const handleKaeltemittelChange = (value) => {
    setKaeltemittel(value);
    if (GWP_WERTE[value]) {
      setGwpWert(GWP_WERTE[value].toString());
    }
  };

  const handleVorlageChange = (kategorie, punktName, value) => {
    const key = `${kategorie}_${punktName}`;
    setVorlageDaten({ ...vorlageDaten, [key]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Konvertiere Vorlagendaten in Messwerte-Format
      const messwerte = {};
      const austauschteile = [];
      let durchgefuehrteArbeiten = '';
      let maengel = '';

      Object.entries(vorlageDaten).forEach(([key, value]) => {
        if (value) {
          const cleanKey = key.split('_').slice(1).join('_');
          messwerte[cleanKey] = value;
        }
      });

      // Spezielle Behandlung für Dokumentationsfelder
      if (messwerte['Durchgeführte Arbeiten']) {
        durchgefuehrteArbeiten = messwerte['Durchgeführte Arbeiten'];
        delete messwerte['Durchgeführte Arbeiten'];
      }
      if (messwerte['Festgestellte Mängel']) {
        maengel = messwerte['Festgestellte Mängel'];
        delete messwerte['Festgestellte Mängel'];
      }

      // Füge Kältemittel-Daten zu messwerten hinzu
      if (kaeltemittel) messwerte['Kältemitteltyp'] = kaeltemittel;
      if (kaeltemittelmenge) messwerte['Füllmenge (kg)'] = kaeltemittelmenge;
      if (gwpWert) messwerte['GWP-Wert'] = gwpWert;
      if (co2Aequivalent) messwerte['CO₂-Äquivalent (t)'] = co2Aequivalent;

      const wartungData = {
        anlage_id: anlageId,
        wartungsdatum: formData.wartungsdatum,
        durchgefuehrte_arbeiten: durchgefuehrteArbeiten || 'Wartung gemäß Vorlage durchgeführt',
        betriebsstunden: parseInt(vorlageDaten['Technische Prüfungen_Betriebsstunden'] || 0) || null,
        messwerte: messwerte,
        austauschteile: austauschteile,
        maengel: maengel || null
      };

      await wartungenAPI.create(wartungData);
      
      // Aktualisiere Anlage mit neuen Kältemittel-Daten falls geändert
      if (kaeltemittel || kaeltemittelmenge) {
        const anlageUpdate = {};
        if (kaeltemittel && kaeltemittel !== anlage.kaeltemittel) {
          anlageUpdate.kaeltemittel = kaeltemittel;
        }
        if (kaeltemittelmenge && kaeltemittelmenge !== anlage.kaeltemittelmenge) {
          anlageUpdate.kaeltemittelmenge = parseFloat(kaeltemittelmenge);
        }
        
        if (Object.keys(anlageUpdate).length > 0) {
          await anlagenAPI.update(anlageId, anlageUpdate);
        }
      }
      
      navigate(`/anlage/${anlageId}`);
    } catch (error) {
      console.error('Fehler beim Speichern:', error);
      setError(error.response?.data?.detail || 'Fehler beim Speichern der Wartung');
    } finally {
      setLoading(false);
    }
  };

  const renderFeld = (kategorie, punkt) => {
    const key = `${kategorie}_${punkt.name}`;
    const value = vorlageDaten[key] || '';

    switch (punkt.typ) {
      case 'text':
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleVorlageChange(kategorie, punkt.name, e.target.value)}
            required={punkt.pflicht}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder={punkt.hinweis || ''}
          />
        );
      
      case 'number':
        return (
          <div className="flex gap-2">
            <input
              type="number"
              step="any"
              value={value}
              onChange={(e) => handleVorlageChange(kategorie, punkt.name, e.target.value)}
              required={punkt.pflicht}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder={punkt.hinweis || ''}
            />
            {punkt.einheit && (
              <span className="px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-700">
                {punkt.einheit}
              </span>
            )}
          </div>
        );
      
      case 'select':
        return (
          <select
            value={value}
            onChange={(e) => handleVorlageChange(kategorie, punkt.name, e.target.value)}
            required={punkt.pflicht}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">-- Bitte wählen --</option>
            {punkt.optionen.map(option => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        );
      
      case 'checkbox':
        return (
          <input
            type="checkbox"
            checked={value === true || value === 'true'}
            onChange={(e) => handleVorlageChange(kategorie, punkt.name, e.target.checked)}
            className="w-6 h-6 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
          />
        );
      
      case 'date':
        return (
          <input
            type="date"
            value={value}
            onChange={(e) => handleVorlageChange(kategorie, punkt.name, e.target.value)}
            required={punkt.pflicht}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        );
      
      case 'textarea':
        return (
          <textarea
            value={value}
            onChange={(e) => handleVorlageChange(kategorie, punkt.name, e.target.value)}
            required={punkt.pflicht}
            rows={4}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder={punkt.hinweis || ''}
          />
        );
      
      default:
        return null;
    }
  };

  if (!anlage || !vorlage) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center py-12">
            <div className="text-lg text-gray-600">Laden...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="container mx-auto px-4 py-8 max-w-5xl">
        <button
          onClick={() => navigate(`/anlage/${anlageId}`)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition"
        >
          <ArrowLeft className="w-5 h-5" />
          Zurück zur Anlage
        </button>

        <div className="bg-white rounded-xl shadow-sm p-8">
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Wartungsprotokoll nach deutschen Vorschriften
            </h1>
            <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-700">
                <strong>Dieses Protokoll entspricht:</strong> 
                {anlage.anlagentyp === 'Kälteanlage' && ' F-Gase-Verordnung (EU) 2024/573, VDMA 24186-1'}
                {anlage.anlagentyp === 'Klimaanlage' && ' F-Gase-Verordnung (EU) 2024/573, VDI 6022 (Hygiene), VDMA 24186-6'}
                {anlage.anlagentyp === 'Lüftungsanlage' && ' VDI 6022 (Hygiene), VDI 2050, VDI 3803'}
                {anlage.anlagentyp === 'Klimakammer' && ' F-Gase-Verordnung (EU) 2024/573, DIN 12880 (Klimaprüfschränke), DIN EN 60068 (Umweltprüfungen)'}
                {anlage.anlagentyp === 'Wärmepumpe' && ' F-Gase-Verordnung (EU) 2024/573, VDI 4645 (Wärmepumpen), VDMA 24020, FW 309 Teil 1'}
              </div>
            </div>
          </div>

          {/* Kunden-Information */}
          {kunde && (
            <div className="mb-8 border border-gray-200 rounded-lg p-6 bg-gray-50">
              <div className="flex items-center gap-2 mb-4">
                <User className="w-5 h-5 text-gray-700" />
                <h2 className="text-xl font-semibold text-gray-900">Kunden-Information</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-gray-700">Firma:</span>
                  <span className="ml-2 text-gray-900">{kunde.firma}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Kundennummer:</span>
                  <span className="ml-2 text-gray-900">{kunde.kundennummer}</span>
                </div>
                {kunde.strasse && (
                  <div className="md:col-span-2">
                    <span className="font-medium text-gray-700">Adresse:</span>
                    <span className="ml-2 text-gray-900">
                      {kunde.strasse}, {kunde.plz} {kunde.ort}
                      {kunde.land && kunde.land !== 'Deutschland' && `, ${kunde.land}`}
                    </span>
                  </div>
                )}
                {kunde.ansprechpartner && (
                  <div>
                    <span className="font-medium text-gray-700">Ansprechpartner:</span>
                    <span className="ml-2 text-gray-900">{kunde.ansprechpartner}</span>
                  </div>
                )}
                {kunde.telefon && (
                  <div>
                    <span className="font-medium text-gray-700">Telefon:</span>
                    <span className="ml-2 text-gray-900">{kunde.telefon}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Anlagen-Information */}
          <div className="mb-8 border border-gray-200 rounded-lg p-6 bg-gray-50">
            <div className="flex items-center gap-2 mb-4">
              <Building2 className="w-5 h-5 text-gray-700" />
              <h2 className="text-xl font-semibold text-gray-900">Anlagen-Information</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              {anlage.lec_anlagennummer && (
                <div>
                  <span className="font-medium text-gray-700">LEC-Anl.-Nr.:</span>
                  <span className="ml-2 text-gray-900">{anlage.lec_anlagennummer}</span>
                </div>
              )}
              {anlage.anlagenbezeichnung && (
                <div>
                  <span className="font-medium text-gray-700">Bezeichnung:</span>
                  <span className="ml-2 text-gray-900">{anlage.anlagenbezeichnung}</span>
                </div>
              )}
              <div>
                <span className="font-medium text-gray-700">Hersteller:</span>
                <span className="ml-2 text-gray-900">{anlage.hersteller}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Typ/Modell:</span>
                <span className="ml-2 text-gray-900">{anlage.modell}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Baujahr:</span>
                <span className="ml-2 text-gray-900">{anlage.baujahr}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Seriennummer:</span>
                <span className="ml-2 text-gray-900">{anlage.seriennummer}</span>
              </div>
              {anlage.standort1 && (
                <div>
                  <span className="font-medium text-gray-700">Standort 1:</span>
                  <span className="ml-2 text-gray-900">{anlage.standort1}</span>
                </div>
              )}
              {anlage.standort2 && (
                <div>
                  <span className="font-medium text-gray-700">Standort 2:</span>
                  <span className="ml-2 text-gray-900">{anlage.standort2}</span>
                </div>
              )}
              
              {/* Kältemittel-Daten (editierbar) */}
              <div className="md:col-span-2 border-t border-gray-300 pt-4 mt-2">
                <h3 className="font-semibold text-gray-800 mb-3">Kältemittel-Daten</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Kältemitteltyp
                    </label>
                    <select
                      value={kaeltemittel}
                      onChange={(e) => handleKaeltemittelChange(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                    >
                      <option value="">-- Bitte wählen --</option>
                      {Object.keys(GWP_WERTE).map(km => (
                        <option key={km} value={km}>{km}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Füllmenge (kg)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={kaeltemittelmenge}
                      onChange={(e) => setKaeltemittelmenge(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      GWP-Wert
                    </label>
                    <input
                      type="number"
                      value={gwpWert}
                      onChange={(e) => setGwpWert(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                      placeholder="Wird automatisch gesetzt"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      CO₂-Äquivalent (t)
                    </label>
                    <input
                      type="text"
                      value={co2Aequivalent}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-green-50 text-green-900 font-semibold text-sm"
                      placeholder="Wird berechnet"
                    />
                  </div>
                </div>
                {co2Aequivalent && (
                  <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">
                    <strong>Berechnung:</strong> {kaeltemittelmenge} kg × {gwpWert} GWP ÷ 1000 = {co2Aequivalent} t CO₂-Äquivalent
                  </div>
                )}
              </div>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-8">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Wartungsdatum und -uhrzeit *
              </label>
              <input
                type="datetime-local"
                value={formData.wartungsdatum}
                onChange={(e) => setFormData({ ...formData, wartungsdatum: e.target.value })}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Vorlagen-Kategorien */}
            {vorlage.pruefpunkte && vorlage.pruefpunkte.map((kategorie) => (
              <div key={kategorie.kategorie} className="border border-gray-200 rounded-lg p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  {kategorie.kategorie}
                </h2>

                <div className="space-y-4">
                  {kategorie.punkte.map(punkt => (
                    <div key={punkt.name}>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {punkt.name}
                        {punkt.pflicht && <span className="text-red-600 ml-1">*</span>}
                        {punkt.hinweis && (
                          <span className="ml-2 text-xs text-gray-500">({punkt.hinweis})</span>
                        )}
                      </label>
                      {renderFeld(kategorie.kategorie, punkt)}
                    </div>
                  ))}
                </div>
              </div>
            ))}

            <div className="flex justify-end gap-4 pt-6 border-t">
              <button
                type="button"
                onClick={() => navigate(`/anlage/${anlageId}`)}
                className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
              >
                Abbrechen
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition disabled:opacity-50"
              >
                <Save className="w-5 h-5" />
                {loading ? 'Wird gespeichert...' : 'Wartung speichern'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default WartungFormNeu;
